import { waapi } from "animejs";

import { TRANSITION_FAST_MS, TRANSITION_SLOW_MS } from "@frontend/styles";
import { originDelta } from "@frontend/utils/originDelta";
import type { OriginTransition } from "@frontend/utils/originTransition";

const CENTERED = "translate(-50%, -50%)";

function restingTransform(el: HTMLElement): string {
  const declared = getComputedStyle(el)
    .getPropertyValue("--resting-transform")
    .trim();

  return "" === declared ? CENTERED : declared;
}

function withResting(resting: string, extra: string): string {
  return "none" === resting ? extra : `${resting} ${extra}`;
}

function originTransform(
  resting: string,
  origin: DOMRect | null,
  popupRect: DOMRect,
) {
  if (!origin) {
    return withResting(resting, "scale(0.96)");
  }

  const { dx, dy, scaleX, scaleY } = originDelta(origin, popupRect);

  return withResting(
    resting,
    `translate(${dx}px, ${dy}px) scale(${scaleX}, ${scaleY})`,
  );
}

export const growFromOrigin: OriginTransition = {
  enter(el, origin) {
    const resting = restingTransform(el);

    waapi.animate(el, {
      opacity: [0, 1],
      transform: [
        originTransform(resting, origin, el.getBoundingClientRect()),
        resting,
      ],
      duration: TRANSITION_FAST_MS,
      ease: "outQuad",
    });
  },
  exit(el, origin) {
    const resting = restingTransform(el);

    waapi.animate(el, {
      opacity: [1, 0],
      transform: [
        resting,
        originTransform(resting, origin, el.getBoundingClientRect()),
      ],
      duration: TRANSITION_FAST_MS,
      ease: "inQuad",
    });
  },
};

export const zoomFromCenter: OriginTransition = {
  enter(el) {
    const resting = restingTransform(el);

    waapi.animate(el, {
      opacity: [0, 1],
      transform: [withResting(resting, "scale(0.96)"), resting],
      duration: TRANSITION_FAST_MS,
      ease: "outQuad",
    });
  },
  exit(el) {
    const resting = restingTransform(el);

    waapi.animate(el, {
      opacity: [1, 0],
      transform: [resting, withResting(resting, "scale(0.96)")],
      duration: TRANSITION_FAST_MS,
      ease: "inQuad",
    });
  },
};

export const fadeOnly: OriginTransition = {
  enter(el) {
    waapi.animate(el, {
      opacity: [0, 1],
      duration: TRANSITION_FAST_MS,
      ease: "outQuad",
    });
  },
  exit(el) {
    waapi.animate(el, {
      opacity: [1, 0],
      duration: TRANSITION_FAST_MS,
      ease: "inQuad",
    });
  },
};

export const bounceFromOrigin: OriginTransition = {
  enter(el, origin) {
    const resting = restingTransform(el);

    waapi.animate(el, {
      opacity: [0, 1],
      transform: [
        originTransform(resting, origin, el.getBoundingClientRect()),
        resting,
      ],
      duration: TRANSITION_SLOW_MS,
      ease: "outElastic(1, .6)",
    });
  },
  exit(el, origin) {
    const resting = restingTransform(el);

    waapi.animate(el, {
      opacity: [1, 0],
      transform: [
        resting,
        originTransform(resting, origin, el.getBoundingClientRect()),
      ],
      duration: TRANSITION_FAST_MS,
      ease: "inQuad",
    });
  },
};

const BLUR_AMOUNT = "6px";

export const blurIn: OriginTransition = {
  enter(el) {
    waapi.animate(el, {
      opacity: [0, 1],
      filter: [`blur(${BLUR_AMOUNT})`, "blur(0px)"],
      duration: TRANSITION_FAST_MS,
      ease: "outQuad",
    });
  },
  exit(el) {
    waapi.animate(el, {
      opacity: [1, 0],
      filter: ["blur(0px)", `blur(${BLUR_AMOUNT})`],
      duration: TRANSITION_FAST_MS,
      ease: "inQuad",
    });
  },
};
